import React from "react";
import ProgramTemplate from "@/components/programs/ProgramTemplate";

const Foundations = () => (
  <ProgramTemplate
    pageKey="foundations"
    breadcrumbLabel="Foundations"
    overline="Program 01 · Foundations"
    headline="Learn Adaptive Physiology"
    headlineAccent="from the inside."
    audience="For individuals meeting the framework for the first time"
    lead="Foundations is an educational, structured introduction to Adaptive Physiology™ — a way to understand the methodology, recognize your own physiological state, and begin applying the framework at your own pace."
    loopHighlight="input"
    whyExists={{
      title: "Understanding comes before intervention.",
      paragraphs: [
        "Most people meet Adaptive Physiology in a moment of need — recovery, burnout, a plateau. Foundations exists for those who want to understand the framework first, and let the intervention take shape from there.",
        "It is not a light version of the flagship program. It is an intentionally paced entry point: teach the model, build awareness of one’s own physiology, and establish the foundational habits the rest of the methodology is built on.",
      ],
    }}
    howApplied={{
      title: "The framework, taught before it’s prescribed.",
      intro:
        "Participants learn what each movement of the loop actually is — and begin practicing it in low-stakes, high-clarity ways.",
      steps: [
        {
          title: "Baseline self-awareness",
          body: "Practical tools to observe HRV, sleep, energy, and behavioral patterns — building the reference point the loop requires.",
        },
        {
          title: "Foundational stimulus",
          body: "Aerobic base, breath, and simple movement inputs the nervous system reliably responds to. Nothing exotic. Everything deliberate.",
        },
        {
          title: "Weekly rhythm",
          body: "Structured cadence that reinforces the signal without overloading the system. Consistency is the mechanism.",
        },
        {
          title: "First adaptive shifts",
          body: "The initial changes in sleep, energy, and reactivity that signal the underlying state is beginning to move.",
        },
      ],
    }}
    experience={{
      title: "A grounded beginning, not a transformation promise.",
      intro:
        "Foundations focuses on the capacities that make everything else possible — regulation, awareness, and behavioral flexibility at the level of an ordinary week.",
      capacities: [
        { label: "Physiological literacy", body: "The ability to read your own state and respond to it appropriately." },
        { label: "Baseline regulation", body: "Access to a settled physiological state without needing a crisis to reach it." },
        { label: "Foundational habits", body: "Small, repeatable inputs the nervous system can adapt to reliably." },
        { label: "Adaptive awareness", body: "Recognizing the moment behavior is being driven by state." },
        { label: "Consistency capacity", body: "The physiological ability to sustain new inputs long enough to matter." },
      ],
    }}
    designedFor={{
      title: "Curious, engaged learners.",
      groups: [
        { title: "Framework-first learners", body: "People who want to understand a system before committing to it." },
        { title: "Early-stage change seekers", body: "Those beginning to notice the state–behavior link in their own life." },
        { title: "Adjacent professionals", body: "Coaches, clinicians, or educators exploring the methodology at a foundational level." },
        { title: "Recovery-adjacent readers", body: "People supporting someone in recovery who want to understand the physiology behind it." },
        { title: "Self-directed practitioners", body: "Those comfortable applying structured guidance without one-to-one support." },
        { title: "Future cohort candidates", body: "Individuals considering Guided Implementation later and wanting a foundation first." },
      ],
    }}
    nextSteps={{
      primary: {
        label: "Express interest in Foundations",
        desc: "A brief note about your context and the enrollment process.",
        to: "/apply",
      },
      secondary: {
        label: "Consider Guided Implementation",
        desc: "The individualized flagship program for deeper, personalized implementation.",
        to: "/programs/guided-implementation",
      },
    }}
  />
);
export default Foundations;
